import { useState, useCallback, useEffect } from 'react';
import { validateUsername, validateDisplayName, validateUsernameWithAvailability } from '@/lib/usernameValidation';

interface UseUsernameValidationOptions {
  checkAvailability?: boolean;
  debounceMs?: number;
}

export function useUsernameValidation(options: UseUsernameValidationOptions = {}) {
  const { checkAvailability = true, debounceMs = 500 } = options;
  
  const [username, setUsername] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [lastChecked, setLastChecked] = useState<string>('');

  const validate = useCallback(async (value: string) => {
    if (!value || value.trim().length === 0) {
      setIsValid(false);
      setError(null);
      setSuggestions([]);
      return;
    }

    // If we're checking the same value, don't re-validate
    if (value === lastChecked) {
      return;
    }

    setIsChecking(true);
    setLastChecked(value);

    try {
      const result = checkAvailability 
        ? await validateUsernameWithAvailability(value)
        : validateUsername(value);

      setIsValid(result.isValid);
      setError(result.error || null);
      setSuggestions(result.suggestions || []);
    } catch (error) {
      console.error('Username validation error:', error);
      setIsValid(false);
      setError('Error checking username availability');
      setSuggestions([]);
    } finally {
      setIsChecking(false);
    }
  }, [checkAvailability, lastChecked]);

  // Debounced validation
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      validate(username);
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [username, validate, debounceMs]);

  const reset = useCallback(() => {
    setUsername('');
    setIsValid(false);
    setIsChecking(false);
    setError(null);
    setSuggestions([]);
    setLastChecked('');
  }, []);

  return {
    username,
    setUsername,
    isValid,
    isChecking,
    error,
    suggestions,
    reset
  };
}

export function useDisplayNameValidation() {
  const [displayName, setDisplayName] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  const validate = useCallback((value: string) => {
    if (!value || value.trim().length === 0) {
      setIsValid(false);
      setError(null);
      setSuggestions([]);
      return;
    }

    const result = validateDisplayName(value);
    setIsValid(result.isValid);
    setError(result.error || null);
    setSuggestions(result.suggestions || []);
  }, []);

  // Validate on change
  useEffect(() => {
    validate(displayName);
  }, [displayName, validate]);

  const reset = useCallback(() => {
    setDisplayName('');
    setIsValid(false);
    setError(null);
    setSuggestions([]);
  }, []);

  return {
    displayName,
    setDisplayName,
    isValid,
    error,
    suggestions,
    reset
  };
}
